import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Space,
  Tag,
  Alert,
  Avatar,
  Descriptions,
  Divider,
  Typography
} from 'antd';
import { Helmet } from 'react-helmet';
import { SearchOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { Title, Text } = Typography;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [categories, setCategories] = useState([]);
const [childCategories, setChildCategories] = useState([]);
const [subchildCategories, setSubchildCategories] = useState([]);

  const PROFILE_PHOTO_BASE_URL = 'https://www.startupbusinesspartners.com/uploads/profile-photos/';

  const clearMessages = () => {
    setSuccessMsg('');
    setErrorMsg('');
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (searchedColumn === dataIndex && searchText) {
        const regex = new RegExp(searchText, 'gi');
        const parts = String(text).split(regex);
        return (
          <>
            {parts.map((part, index) =>
              index > 0 ? (
                <span key={index} style={{ backgroundColor: '#ffc069' }}>
                  {searchText}
                </span>
              ) : (
                part
              )
            )}
          </>
        );
      }
      return text;
    },
  });

  const uploadProps = {
    name: 'image',
    action: `${api.defaults.baseURL}/upload`,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    fileList,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return false;
      }
      return true;
    },
    onChange: (info) => {
      setFileList(info.fileList);
      if (info.file.status === 'uploading') {
        setUploading(true);
        return;
      }
      if (info.file.status === 'done') {
        setUploading(false);
        if (info.file.response && info.file.response.success) {
          message.success('Profile photo uploaded successfully!');
          form.setFieldsValue({
            profile_photo: info.file.response.filename
          });
        } else {
          message.error(info.file.response?.message || 'Upload failed!');
        }
      }
      if (info.file.status === 'error') {
        setUploading(false);
        message.error('Upload failed!');
      }
    },
    onRemove: () => {
      form.setFieldsValue({
        profile_photo: null
      });
      setFileList([]);
    },
  };

  const columns = [
{
  title: 'S.No.',
  key: 'sno',
  width: 80,
  render: (_text, _record, index) =>
    index + 1 + (pagination.current - 1) * pagination.pageSize,
},

    {
      title: 'Photo',
      dataIndex: 'profile_photo',
      key: 'profile_photo',
      width: 80,
      render: (profile_photo, record) => (
        <Avatar
          size={40}
          src={profile_photo ? `${PROFILE_PHOTO_BASE_URL}${profile_photo}` : null}
        >
          {!profile_photo && record.name?.charAt(0)?.toUpperCase()}
        </Avatar>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Mobile No.',
      dataIndex: 'contact_no',
      key: 'contact_no',
      ...getColumnSearchProps('contact_no'),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      ...getColumnSearchProps('username'),
    },
    {
      title: 'Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (user_type) => (
        <Tag color={user_type === 'admin' ? 'red' : 'blue'}>{user_type}</Tag>
      ),
      ...getColumnSearchProps('user_type'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === 'approved' ? 'green' : status === 'pending' ? 'orange' : 'red'
          }
        >
          {status}
        </Tag>
      ),
      ...getColumnSearchProps('status'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 350,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button 
            size="small" 
            danger 
            onClick={() => handleDelete(record.user_id)}
            loading={loading}
          >
            Delete
          </Button>
          {record.status !== 'approved' && (
            <Button 
              size="small" 
              type="primary" 
              onClick={() => handleApprove(record.user_id)}
              loading={loading}
            >
              Approve
            </Button>
          )}
          {record.status === 'approved' && (
            <Button 
              size="small" 
              type="default" 
              onClick={() => handleSuspend(record.user_id)}
            >
              Suspend
            </Button>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);
  
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

// Fetch categories data FIRST, then fetch users
useEffect(() => {
  const fetchData = async () => {
    try {
      // Fetch categories first
      const [catRes, childRes, subchildRes] = await Promise.all([
        api.get('/categories'),
        api.get('/child-categories'),
        api.get('/subchild-categories'),
      ]);

      setCategories(catRes.data || []);
      setChildCategories(childRes.data || []);
      setSubchildCategories(subchildRes.data || []);

      // Now fetch users AFTER categories are set
      await fetchUsers(catRes.data, childRes.data, subchildRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  fetchData();
}, []);

// Update fetchUsers to accept categories as parameters
const fetchUsers = async (catData = categories, childData = childCategories, subchildData = subchildCategories) => {
  setLoading(true);
  try {
    const response = await api.get(`/users/get-all-users`);
    if (response.data.success) {
      const usersWithCategoryNames = response.data.users.map(user => ({
        ...user,
        parent_category_name: catData.find(cat => cat.id === user.parent_category_id)?.name || null,
        child_category_name: childData.find(cat => cat.id === user.child_category_id)?.name || null,
        subchild_category_name: subchildData.find(cat => cat.id === user.subchild_category_id)?.name || null,
      }));
      setUsers(usersWithCategoryNames || []);
    } else {
      setErrorMsg('Failed to fetch users');
    }
  } catch (error) {
    setErrorMsg('Failed to fetch users: ' + (error.response?.data?.message || error.message));
  } finally {
    setLoading(false);
  }
};

// Remove the getCategoryName function as we're using direct find

  const handleView = (user) => {
    setSelectedUser(user);
    setViewModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      clearMessages();
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name,
        email: values.email,
        user_type: values.user_type,
        contact_no: values.contact_no || null,
        username: values.username || null,
        profile_photo: values.profile_photo || null,
        ...(values.password ? { password: values.password } : {}),
      };

      let response;
      if (editingId) {
        response = await api.put(`/users/${editingId}`, payload);
      } else {
        response = await api.post('/users', payload);
      }

      if (response.data && response.data.success) {
        setSuccessMsg(response.data.message || 
          (editingId ? 'User updated successfully' : 'User created successfully'));
        setVisible(false);
        form.resetFields();
        setFileList([]);
        setEditingId(null);
        await fetchUsers();
      } else {
        setErrorMsg(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || error.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    clearMessages();
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      user_type: user.user_type,
      contact_no: user.contact_no,
      username: user.username,
      profile_photo: user.profile_photo,
      password: '',
    });

    if (user.profile_photo) {
      setFileList([{
        uid: '-1',
        name: user.profile_photo,
        status: 'done',
        url: `${PROFILE_PHOTO_BASE_URL}${user.profile_photo}`,
      }]);
    } else {
      setFileList([]);
    }

    setEditingId(user.user_id);
    setVisible(true);
  };

  const handleDelete = async (user_id) => {
    clearMessages();
    const confirmed = window.confirm('Are you sure you want to delete this user? This action cannot be undone.');
    
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await api.delete(`/users/${user_id}`);
      if (response.data && response.data.success) {
        setSuccessMsg('User deleted successfully');
        await fetchUsers();
      } else {
        setErrorMsg(response.data?.message || 'Failed to delete user');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || error.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user_id) => {
    clearMessages();
    try {
      setLoading(true);
      const response = await api.put(`/users/${user_id}/approve`);
      if (response.data && response.data.success) {
        setSuccessMsg('User approved successfully');
        await fetchUsers();
      } else {
        setErrorMsg(response.data?.message || 'Failed to approve user');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || error.message || 'Failed to approve user');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (user_id) => {
    clearMessages();
    try {
      setLoading(true);
      const response = await api.put(`/users/${user_id}/suspend`);
      if (response.data && response.data.success) {
        setSuccessMsg('User suspended successfully');
        await fetchUsers();
      } else {
        setErrorMsg(response.data?.message || 'Failed to suspend user');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || error.message || 'Failed to suspend user');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Render user details based on user type
  const renderUserDetails = () => {
    if (!selectedUser) return null;
    
    const { user_type } = selectedUser;
    
    return (
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px' }}>
        <Descriptions title="Basic Information" bordered column={1}>
          <Descriptions.Item label="User ID">{selectedUser.user_id || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Name">{selectedUser.name || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Email">{selectedUser.email || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Username">{selectedUser.username || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Contact No">{selectedUser.contact_no || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Age">{selectedUser.age || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="User Type">{selectedUser.user_type || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={selectedUser.status === 'approved' ? 'green' : selectedUser.status === 'pending' ? 'orange' : 'red'}>
              {selectedUser.status || 'N/A'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Registration Date">
            {formatDate(selectedUser.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {formatDate(selectedUser.updated_at)}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Address Information */}
        <Descriptions title="Address Information" bordered column={1}>
          <Descriptions.Item label="Address">{selectedUser.address || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="City">{selectedUser.city || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="State">{selectedUser.state || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Pincode">{selectedUser.pincode || 'N/A'}</Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Category Information */}
        <Descriptions title="Category Information" bordered column={1}>
          <Descriptions.Item label="Parent Category">{selectedUser.parent_category_name  || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Child Category">{selectedUser.child_category_name  || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Subchild Category">{selectedUser.subchild_category_name || 'N/A'}</Descriptions.Item>
          {selectedUser.other_category && <Descriptions.Item label="Other Category">{selectedUser.other_category}</Descriptions.Item>}
          {selectedUser.other_child_category && <Descriptions.Item label="Other Child Category">{selectedUser.other_child_category}</Descriptions.Item>}
          {selectedUser.other_subchild_category && <Descriptions.Item label="Other Subchild Category">{selectedUser.other_subchild_category}</Descriptions.Item>}
        </Descriptions>

        <Divider />

        {/* User type specific details */}
        {user_type === '3rd Party Services' && (
          <>
            <Title level={4}>3rd Party Services Details</Title>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Service Type">{selectedUser.service_type || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Sub Service Type">{selectedUser.sub_service_type || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Years of Experience">{selectedUser.years_of_experience || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Expertise">{selectedUser.expertise || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Rating">{selectedUser.rating || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </>
        )}

        {user_type === 'Partner' && (
          <>
            <Title level={4}>Partner Details</Title>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Business Type">{selectedUser.business_type || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Date of Birth">{formatDate(selectedUser.dob)}</Descriptions.Item>
              <Descriptions.Item label="Gender">{selectedUser.gender || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Aadhaar Number">{selectedUser.aadhaar_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Identity Number">{selectedUser.identity_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="PAN Number">{selectedUser.pan_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Professional Background">{selectedUser.professional_background || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Industry Experience">{selectedUser.industry_experience || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Financial Capability">{selectedUser.financial_capability || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Funding Source">{selectedUser.funding_source || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Skills">{selectedUser.skills || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Ownership Preference">{selectedUser.ownership_preference || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Time Commitment">{selectedUser.time_commitment || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Business Status">{selectedUser.business_status || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Past Partnership Experience">{selectedUser.past_partnership_experience || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Legal Disputes">{selectedUser.legal_disputes || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Future Vision">{selectedUser.future_vision || 'N/A'}</Descriptions.Item>
              {selectedUser.identity_proof_file && (
                <Descriptions.Item label="Identity Proof File">
                  <a href={selectedUser.identity_proof_file} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}

        {user_type === 'Employee' && (
          <>
            <Title level={4}>Employee Details</Title>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Date of Birth">{formatDate(selectedUser.dob)}</Descriptions.Item>
              <Descriptions.Item label="Gender">{selectedUser.gender || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Permanent Address">{selectedUser.permanent_address || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Aadhaar Number">{selectedUser.aadhaar_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="PAN Number">{selectedUser.pan_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Highest Qualification">{selectedUser.highest_qualification || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Certificate Details">{selectedUser.certificate_details || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Skills">{selectedUser.job_skills || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Years of Experience">{selectedUser.years_of_experience || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Expected Salary">{selectedUser.expected_salary || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Joining Availability">{selectedUser.joining_availability || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Emergency Contact Name">{selectedUser.emergency_contact_name || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Emergency Contact Relation">{selectedUser.emergency_contact_relation || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Emergency Contact Number">{selectedUser.emergency_contact_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Emergency Contact Address">{selectedUser.emergency_contact_address || 'N/A'}</Descriptions.Item>
              {selectedUser.certificate_file && (
                <Descriptions.Item label="Certificate File">
                  <a href={selectedUser.certificate_file} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}

        {user_type === 'Business' && (
          <>
            <Title level={4}>Business Details</Title>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Business Name">{selectedUser.business_name || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Business Model">{selectedUser.business_model || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Established Year">{selectedUser.established_year || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Reason for Sale">{selectedUser.reason_for_sale || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Asking Price">{selectedUser.asking_price || 'N/A'} {selectedUser.asking_price_unit || ''}</Descriptions.Item>
              <Descriptions.Item label="Annual Turnover">{selectedUser.annual_turnover || 'N/A'} {selectedUser.annual_turnover_unit || ''}</Descriptions.Item>
              <Descriptions.Item label="Net Profit">{selectedUser.net_profit || 'N/A'} {selectedUser.net_profit_unit || ''}</Descriptions.Item>
              <Descriptions.Item label="Assets Included">{selectedUser.assets_included || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Liabilities">{selectedUser.liabilities || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Number of Employees">{selectedUser.number_of_employees || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Customer Base">{selectedUser.customer_base || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Ongoing Projects">{selectedUser.ongoing_projects || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Franchise Validity">{selectedUser.franchise_validity || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Business Registration Type">{selectedUser.business_registration_type || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Registration Number">{selectedUser.registration_number || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Lease Details">{selectedUser.lease_details || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Preferred Contact Mode">{selectedUser.preferred_contact_mode || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Price Negotiable">{selectedUser.price_negotiable || 'N/A'}</Descriptions.Item>
              {selectedUser.ownership_proof_file && (
                <Descriptions.Item label="Ownership Proof File">
                  <a href={selectedUser.ownership_proof_file} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                </Descriptions.Item>
              )}
              {selectedUser.business_photo && (
                <Descriptions.Item label="Business Photo">
                  <a href={selectedUser.business_photo} target="_blank" rel="noopener noreferrer">
                    View Photo
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>User Management</title>
      </Helmet>
      
      <div className="user-management" style={{ padding: '20px' }}>
        {successMsg && (
          <Alert
            message={successMsg}
            type="success"
            showIcon
            closable
            onClose={() => setSuccessMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

<Table
  columns={columns}
  dataSource={users}
  rowKey="user_id"
  loading={loading}
  pagination={{
    current: pagination.current,
    pageSize: pagination.pageSize,
    onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
  }}
  scroll={{ x: 'max-content' }}
/>


        <Modal
          title={editingId ? 'Edit User' : 'Create User'}
          visible={visible}
          onOk={handleSubmit}
          onCancel={() => {
            setVisible(false);
            setFileList([]);
            setEditingId(null);
          }}
          confirmLoading={loading}
          destroyOnClose
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the name!' }]}
            >
              <Input />
            </Form.Item>
            
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please input the email!' },
                { type: 'email', message: 'Please input a valid email!' },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="user_type"
              label="User Type"
              rules={[{ required: true, message: 'Please select the user type!' }]}
            >
              <Select placeholder="Select user type">
                <Option value="3rd Party Services">3rd Party Services</Option>
                <Option value="Partner">Partner</Option>
                <Option value="Employee">Skilled Business Employee</Option>
                <Option value="Business">Business For Sale</Option>
              </Select>
            </Form.Item>

            <Form.Item name="contact_no" label="Contact No.">
              <Input />
            </Form.Item>

            <Form.Item name="username" label="Username">
              <Input />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={editingId ? [] : [{ required: true, message: 'Please input the password!' }]}
              extra={editingId ? "Leave blank if you don't want to change the password" : ""}
            >
              <Input.Password />
            </Form.Item>

            <Form.Item name="profile_photo" label="Profile Photo" hidden>
              <Input />
            </Form.Item>

            <Form.Item label="Upload Profile Photo">
              <Upload {...uploadProps} maxCount={1}>
                <Button icon={<UploadOutlined />} loading={uploading}>
                  {uploading ? 'Uploading...' : 'Click to Upload'}
                </Button>
              </Upload>
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                Supported formats: JPG, PNG, GIF (Max: 2MB)
              </div>
            </Form.Item>
          </Form>
        </Modal>

        {/* View User Details Modal */}
        <Modal
          title={`User Details - ${selectedUser?.name || ''}`}
          visible={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>
          ]}
          width={900}
          style={{ top: 20 }}
          bodyStyle={{ padding: 0 }}
        >
          {selectedUser && renderUserDetails()}
        </Modal>
      </div>
    </>
  );
};

export default UserManagement;